// Match Day — HID top-up, live during the match (runs on the device, no connection needed).
// Default = minutes-based estimate; the trainer can overwrite it per player with the HID he reads in
// Sonra. Rule: services/matchday/topup.js (engine.js copy). Only players under the cut-off, runs
// capped at MAX_RUNS. Sorted by minutes, ascending.
import React, { useState } from 'react';
import { RotateCcw, AlertTriangle, Printer } from 'lucide-react';
import { topupTable, metresOrNull, TOPUP_DEFAULTS, formatMinSec } from './engine';
import { setOverride, clearOverride, setTypedTarget } from './matchdayStore';
import { Badge, Btn, C, MetresInput } from './ui';

const BASIS = { estimate: { label: 'estimate', color: 'var(--text-muted)' }, manual: { label: 'Sonra', color: C.blue }, gps: { label: 'GPS', color: C.purple } };
const STATUS = { on: 'on pitch', off: 'subbed off', bench: 'bench' };
const m0 = v => (v == null ? '—' : `${Math.round(v)} m`);

function ResetBtn({ onClick, label }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} title={label}
      style={{ minWidth: 44, minHeight: 44, borderRadius: 10, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <RotateCcw size={18} />
    </button>
  );
}

export default function TopUpTab({ state, commit, minutes }) {
  const rows = topupTable({ players: state.players, squad: state.squad, overrides: state.overrides, minutes, gps: state.gps, config: TOPUP_DEFAULTS });
  const hasGps = Object.keys(state.gps || {}).length > 0;
  const [inputError, setInputError] = useState(null);
  const { RUN_LENGTH_M, RUN_RANGE_M, TOPUP_CUTOFF_MIN, MAX_RUNS } = TOPUP_DEFAULTS;

  const onSonra = (playerId, text) => {
    const v = metresOrNull(text);
    if (v === null) return;            // empty: no change (↺ resets)
    if (v === undefined || v < 0) { setInputError('Enter metres, e.g. 312,5'); return; }
    setInputError(null);
    commit(s => setOverride(s, playerId, v));
  };
  const onTarget = (playerId, text) => {
    const v = metresOrNull(text);
    if (v === null) return;
    if (v === undefined || v <= 0) { setInputError('Enter a target in metres per 90, e.g. 800'); return; }
    setInputError(null);
    commit(s => setTypedTarget(s, playerId, v));
  };

  const cell = { padding: '8px 8px', borderBottom: '1px solid var(--border)', verticalAlign: 'middle' };
  return (
    <div className="matchday-topup" style={{ display: 'grid', gap: 12 }}>
      {/* Print: only this table, on white — the sheet the trainer hands to the players. */}
      <style>{`@media print {
        body * { visibility: hidden !important; }
        .matchday-topup, .matchday-topup * { visibility: visible !important; color: #000 !important; }
        .matchday-topup { position: absolute; left: 0; top: 0; width: 100%; background: #fff; }
        .matchday-topup input { border: none !important; background: transparent !important; }
        .matchday-noprint { display: none !important; }
      }`}</style>
      <div className="matchday-noprint" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {inputError && <span style={{ color: C.red, fontWeight: 700 }}>{inputError}</span>}
        {hasGps ? <Badge color={C.purple}>GPS uploaded — used where no Sonra value is entered</Badge> : <Badge>No GPS uploaded yet — estimates</Badge>}
        <Btn tone="ghost" style={{ marginLeft: 'auto' }} onClick={() => window.print()}><Printer size={16} /> Print</Btn>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800 }}>HID top-up · {state.fixture?.homeAway === 'H' ? 'vs' : '@'} {state.fixture?.opponent} · {state.fixture?.matchDate}</div>
      <div style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Under {TOPUP_CUTOFF_MIN} min → top-up, max {MAX_RUNS} runs; players reaching {TOPUP_CUTOFF_MIN} min drop off the list. One run = {RUN_LENGTH_M} m HID (range {RUN_RANGE_M[0]}–{RUN_RANGE_M[1]} m).
        Target = his match target per 90 (position MD target). <b>Sonra:</b> enter <i>High Speed Running (Absolute)</i> of the
        two halves only, not the session total (that includes the warm-up). Name the post-match drill <b>Match-RUNS</b> in Sonra.
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
          <thead>
            <tr style={{ textAlign: 'left', fontSize: 12, textTransform: 'uppercase', color: 'var(--text-muted)' }}>
              <th style={cell}>Player</th><th style={cell}>Min</th><th style={cell}>Target</th>
              <th style={cell}>HID done (Sonra)</th><th style={cell}>Needed</th><th style={{ ...cell, textAlign: 'right' }}>Runs</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const basis = BASIS[r.basis];
              return (
                <tr key={r.playerId}>
                  <td style={cell}>
                    <div style={{ fontWeight: 700 }}>{r.squadNumber != null ? `${r.squadNumber} ` : ''}{r.name}</div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                      <Badge>{r.position || '—'}</Badge>
                      <Badge>{STATUS[r.status]}</Badge>
                      {r.provisional && <Badge color={C.amber}>live</Badge>}
                    </div>
                  </td>
                  <td style={{ ...cell, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {formatMinSec(r.minutes * 60)}
                    {r.gpsMinutesDiff && <div style={{ fontSize: 12, fontWeight: 600, color: C.amber }}>GPS {Math.round(r.gpsMinutes)}′</div>}
                  </td>
                  <td style={cell}>
                    {r.noTarget || r.typedTarget ? (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        {r.noTarget && <Badge color={C.red}>no target</Badge>}
                        <MetresInput value={r.typedTarget ? r.targetM : null} placeholder="m / 90" width={84}
                          ariaLabel={`Target for ${r.name}`} onCommit={t => onTarget(r.playerId, t)} />
                        {r.typedTarget && <ResetBtn label="Back to the position target" onClick={() => commit(s => setTypedTarget(s, r.playerId, null))} />}
                      </div>
                    ) : (
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{m0(r.targetM)}</span>
                    )}
                  </td>
                  <td style={cell}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <MetresInput value={r.basis === 'manual' ? r.doneM : null} placeholder="m" width={90}
                        ariaLabel={`Sonra HID for ${r.name}`} onCommit={t => onSonra(r.playerId, t)} />
                      {r.basis === 'manual' && <ResetBtn label="Clear — back to the estimate" onClick={() => commit(s => clearOverride(s, r.playerId))} />}
                      <Badge color={basis.color}>{basis.label}</Badge>
                    </div>
                    {r.basis === 'manual' && r.estimateM != null && (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>estimate was {m0(r.estimateM)} needed</div>
                    )}
                    {r.gpsMismatch && <div style={{ fontSize: 12, color: C.amber, marginTop: 4 }}>Sonra and GPS differ by more than 10%</div>}
                  </td>
                  <td style={{ ...cell, fontVariantNumeric: 'tabular-nums' }}>{r.required ? m0(r.neededM) : '—'}</td>
                  <td style={{ ...cell, textAlign: 'right' }}>
                    {r.runs == null ? <span style={{ color: 'var(--text-dimmed)' }}>—</span> : (
                      <>
                        <div style={{ fontSize: 34, fontWeight: 800, lineHeight: 1, color: r.capped ? C.amber : 'var(--text-primary)' }}>{r.runs}</div>
                        {r.runs > 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.runsMin === r.runsMax ? `${r.runsMin}` : `${r.runsMin}–${r.runsMax}`}</div>}
                        {r.capped && <div style={{ fontSize: 12, color: C.amber, fontWeight: 700, display: 'inline-flex', gap: 4, alignItems: 'center' }}><AlertTriangle size={12} /> max {MAX_RUNS} (calc. {r.runsUncapped})</div>}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {!rows.length && (
              <tr><td style={cell} colSpan={6}>No outfield players under {TOPUP_CUTOFF_MIN} min.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
